import { useContext, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import TaskHeaderOptions from '@atom/components/common/workOrderDetail/taskOptions/TaskHeaderOptions';
import WorkOrderPreviewContext, {
  WorkOrderActionTypes,
} from '@atom/components/workOrderPreview/WorkOrderPreviewContext';
import { TASK_UPDATE } from '@atom/graph/task';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Icon, IconButton, Modal, Snackbar } from '@atom/mui';
import {
  CheckCircleIcon,
  CheckCircleOutlineIcon,
  ErrorOutlineIcon,
  ExpandMoreIcon,
} from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { Task } from '@atom/types/task';
import {
  doesNotHaveRolePermissions,
  ROLE_SETS,
} from '@atom/utilities/authUtilities';
import { removeFromSet } from '@atom/utilities/setUtilities';
import { isManagerRequiredForTaskCompletion } from '@atom/utilities/tenantFeatureUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';
import {
  getValidationsFromError,
  handleRequiredFieldErrorMessage,
} from '@atom/utilities/workValidationUtilities';

import classes from './collapsedTask.module.css';

interface Props {
  task: Task;
}

const CollapsedTaskHeader = ({ task }: Props) => {
  const [, { updateSearchParams }] = useNavigation({
    view: SearchParamType.STRING,
  });

  const {
    collapsedTasks,
    setCollapsedTasks,
    workOrderDetail,
    refetch,
    dispatch,
  } = useContext(WorkOrderPreviewContext);

  const { workValidations, setWorkValidations } = useWorkValidations();

  const [completeModalOpen, setCompleteModalOpen] = useState<boolean>(false);

  const [taskUpdate, { loading: taskUpdateLoading }] = useMutation(TASK_UPDATE);

  const expandSection = () => {
    setCollapsedTasks(removeFromSet(collapsedTasks, task.id));
  };

  const handleCompleteToggle = async () => {
    try {
      const newCompleteValue = !task.isCompleted;
      const completeText = newCompleteValue ? 'Complete' : 'Incomplete';

      Snackbar.info({ message: `Marking ${task.name} ${completeText}...` });

      await taskUpdate({
        variables: {
          input: {
            id: task.id,
            workOrderId: workOrderDetail.id,
            isCompleted: newCompleteValue,
          },
        },
      });

      Snackbar.info({ message: `Marked ${task.name} ${completeText}.` });

      refetch();
    } catch (err) {
      const errorCode = getErrorCode(err);

      const allValidations = getValidationsFromError(err);

      if (errorCode === 422) {
        setWorkValidations({
          workOrderId: workOrderDetail.id,
          taskValidations: allValidations.mappedTaskValidations,
          workValidations: allValidations.workInfoValidations,
        });
      }

      handleRequiredFieldErrorMessage({
        errorCode,
        complete: true,
        allValidations,
        Snackbar,
        updateSearchParams,
        isPreview: true,
      });
    } finally {
      setCompleteModalOpen(false);
    }
  };

  const completeIcon = task.isCompleted
    ? CheckCircleIcon
    : CheckCircleOutlineIcon;
  const completeColor = task.isCompleted
    ? colors.brand.green
    : colors.neutral.silver;
  const tooltipText = task.isCompleted ? 'Mark Incomplete' : 'Mark Complete';

  const handleCompleteClick = () => {
    return task.isCompleted
      ? handleCompleteToggle()
      : setCompleteModalOpen(true);
  };

  // TODO: [AM-7948]: Move to RBAC
  const disableTaskCompleteButton =
    doesNotHaveRolePermissions(
      isManagerRequiredForTaskCompletion
        ? ROLE_SETS.MANAGER
        : ROLE_SETS.INSPECTOR,
    ) || workOrderDetail.isClosed;

  const hasPendingChanges = useMemo(() => {
    return task.assetIds.some(
      id => workOrderDetail.assets[id]?.hasPendingChanges,
    );
  }, [task]);

  const isTaskInvalid = R.has(task.id)(workValidations?.taskValidations);

  return (
    <>
      <div className={classes.headerContainer}>
        <div className={classes.leftHeaderSection}>
          {isTaskInvalid ? (
            <Icon color={colors.brand.red} icon={ErrorOutlineIcon} />
          ) : (
            <div className={classes.changeIndicatorContainer}>
              <IconButton
                size="small"
                onClick={handleCompleteClick}
                disabled={disableTaskCompleteButton}
                tooltip={tooltipText}
                edge="end"
              >
                <Icon color={completeColor} icon={completeIcon} />
              </IconButton>
              {hasPendingChanges && <div className={classes.changeIndicator} />}
            </div>
          )}
          <div className={classes.taskTitle}>{task.name}</div>
        </div>
        <div className={classes.rightHeaderSection}>
          <TaskHeaderOptions
            workOrderDetail={workOrderDetail}
            task={task}
            refetch={refetch}
            WorkOrderActionTypes={WorkOrderActionTypes}
            dispatch={dispatch}
            dataCyLabel="previewTaskAdditionalActions"
          />
          <IconButton onClick={expandSection} edge="end">
            <Icon icon={ExpandMoreIcon} />
          </IconButton>
        </div>
      </div>
      <Modal
        open={completeModalOpen}
        loading={taskUpdateLoading}
        confirmButtonText="Complete"
        onCancel={() => setCompleteModalOpen(false)}
        onConfirm={handleCompleteToggle}
        title="Complete Task?"
      >
        Not everyone completed the task. This action will complete the task for
        everyone on the task. Are you sure?
      </Modal>
    </>
  );
};

export default CollapsedTaskHeader;
